import * as T from 'three';

const UP = new T.Vector3(0, 1, 0);
const wrap = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
const smooth = (t: number) => t * t * (3 - 2 * t);
type SavedBone = { bone: T.Object3D; position: T.Vector3; rotation: T.Quaternion };
type Step = { time: number; duration: number; from: T.Vector3; to: T.Vector3; rotation: T.Quaternion };
type Leg = {
  name: string; bones: T.Object3D[]; lengths: number[]; sole: T.Vector3;
  restRotation: T.Quaternion; anchorRotation: T.Quaternion; rotation: T.Quaternion;
  rawRotation: T.Quaternion;
  rest: T.Vector3; raw: T.Vector3; target: T.Vector3; anchor: T.Vector3;
  normal: T.Vector3; lift: number; planted: boolean; step: Step | null;
  release: T.Vector3; swingTime: number; error: number;
  support: T.Vector3[];
};
export interface QuadrupedGroundProfile {
  footPart: string;
  clearance?: number;
  anatomicalLegs?: boolean;
  releaseTime?: number;
  checkFootSurface?: boolean;
  gaits: Record<string, { duration: number; duty: number; speed?: number; phases: Record<string, number> }>;
}
export const DEER_GROUND_PROFILE: QuadrupedGroundProfile = {
  footPart: 'Hoof',
  gaits: {
    Walk: { duration: 1.6, duty: .64, phases: { 'Hind.L': 0, 'Fore.L': .25, 'Hind.R': .5, 'Fore.R': .75 } },
    Run: { duration: .8, duty: .29, phases: { 'Hind.L': 0, 'Hind.R': .08, 'Fore.L': .47, 'Fore.R': .56 } },
  },
};
export interface QuadrupedGroundFrame { yaw: number; speed: number; clip: string; time: number }

/** Ground adaptation is a post-animation layer. Stance targets live in world
 * space; only lifted feet follow the actor. All three leg links keep their length. */
export class QuadrupedGrounding {
  private readonly saved: SavedBone[] = [];
  private readonly legs: Leg[] = [];
  private readonly torso: { bone: T.Object3D; weight: number }[] = [];
  private initialized = false;
  private previousYaw = 0;
  private turn = 0;
  private height = 0;
  private compression = 0;
  private slope = new T.Quaternion();
  private stepCount = 0;
  private cooldown = 0;
  private readonly scale: number;

  constructor(readonly model: T.Group, readonly group: T.Group,
    readonly ground: (x: number, z: number) => number,
    readonly profile: QuadrupedGroundProfile = DEER_GROUND_PROFILE) {
    // SkinnedMesh.updateMatrixWorld also refreshes bindMatrixInverse in attached
    // mode. updateWorldMatrix alone would measure the scaled sole twice.
    group.updateMatrixWorld(true); this.scale = model.getWorldScale(new T.Vector3()).x;
    const bone = (name: string) => {
      const result = model.getObjectByName(name) ?? model.getObjectByName(name.replaceAll('.', ''));
      if (!result) throw new Error(`Missing quadruped ground joint: ${name}`);
      return result;
    };
    for (const [name, weight] of [['Pelvis', -.15], ['Spine', .12], ['Chest', .13], ['NeckBase', .06]] as const)
      this.torso.push({ bone: bone(name), weight });
    for (const limb of ['Fore', 'Hind']) for (const side of ['L', 'R']) {
      const bones = ['Upper', 'Lower', 'Cannon', profile.footPart].map(part => bone(limb + part + '.' + side));
      const points = bones.map(b => b.getWorldPosition(new T.Vector3()));
      const hoof = bones[3], restRotation = hoof.getWorldQuaternion(new T.Quaternion());
      // Calibrate the contact point from the actual skinned hoof sole, not an
      // ankle-height guess. Every material primitive contributes to the search.
      const soleVertices: T.Vector3[] = [];
      model.traverse(o => {
        if (!(o instanceof T.SkinnedMesh)) return;
        const index = o.skeleton.bones.indexOf(hoof as T.Bone);
        if (index < 0) return;
        const a = o.geometry.attributes;
        for (let i = 0; i < a.position.count; i++) {
          if (![0, 1, 2, 3].some(k => a.skinIndex.getComponent(i, k) === index && a.skinWeight.getComponent(i, k) > .999)) continue;
          soleVertices.push(o.getVertexPosition(i, new T.Vector3()).applyMatrix4(o.matrixWorld));
        }
      });
      if (!soleVertices.length) throw new Error(`Missing foot sole: ${limb}.${side}`);
      const low = Math.min(...soleVertices.map(p => p.y));
      const bottom = soleVertices.filter(p => p.y < low + .002 * this.scale);
      const contact = bottom.reduce((a, p) => a.add(p), new T.Vector3()).divideScalar(bottom.length); contact.y = low;
      const sole = hoof.worldToLocal(contact.clone());
      const support = profile.checkFootSurface ? soleVertices.filter(p => p.y < low + .04 * this.scale).map(p => hoof.worldToLocal(p.clone()).sub(sole)) : [];
      this.legs.push({ name: limb + '.' + side, bones,
        lengths: points.slice(1).map((p, i) => p.distanceTo(points[i])), sole, restRotation,
        anchorRotation: restRotation.clone(), rotation: restRotation.clone(), rawRotation: restRotation.clone(),
        rest: group.worldToLocal(contact.clone()), raw: contact.clone(), target: contact.clone(), anchor: contact.clone(),
        normal: UP.clone(), lift: 0, planted: true, step: null, release: new T.Vector3(), swingTime: 0, error: 0, support });
    }
    for (const b of new Set([...this.torso.map(j => j.bone), ...this.legs.flatMap(l => l.bones)]))
      this.saved.push({ bone: b, position: b.position.clone(), rotation: b.quaternion.clone() });
  }

  /** Restore before AnimationMixer: constant channels may skip redundant writes. */
  restore(): void {
    for (const p of this.saved) { p.bone.position.copy(p.position); p.bone.quaternion.copy(p.rotation); }
    this.model.position.set(0, 0, 0); this.model.quaternion.identity();
  }
  reset(): void { this.restore(); this.initialized = false; this.turn = this.height = this.compression = this.cooldown = this.stepCount = 0; this.slope.identity(); for (const l of this.legs) l.step = null; }
  private normalAt(x: number, z: number, out: T.Vector3): T.Vector3 {
    const e = .065;
    return out.set(-(this.ground(x + e, z) - this.ground(x - e, z)) / (2 * e), 1,
      -(this.ground(x, z + e) - this.ground(x, z - e)) / (2 * e)).normalize();
  }
  private groundPoint(p: T.Vector3): T.Vector3 { p.y = this.ground(p.x, p.z) + (this.profile.clearance ?? .0015); return p; }
  private footRotation(leg: Leg, yaw: number, point: T.Vector3): T.Quaternion {
    this.normalAt(point.x, point.z, leg.normal);
    return new T.Quaternion().setFromUnitVectors(UP, leg.normal)
      .multiply(new T.Quaternion().setFromAxisAngle(UP, yaw)).multiply(leg.restRotation);
  }

  update(dt: number, frame: QuadrupedGroundFrame): void {
    for (const p of this.saved) { p.position.copy(p.bone.position); p.rotation.copy(p.bone.quaternion); }
    this.group.updateWorldMatrix(true, true);
    const first = !this.initialized;
    const rate = first || dt <= 0 ? 0 : wrap(frame.yaw - this.previousYaw) / dt;
    this.previousYaw = frame.yaw;
    this.turn = T.MathUtils.damp(this.turn, T.MathUtils.clamp(rate, -2.6, 2.6), 8, dt);
    this.cooldown = Math.max(0, this.cooldown - dt);
    for (const leg of this.legs) {
      leg.raw.copy(leg.sole).applyMatrix4(leg.bones[3].matrixWorld);
      leg.bones[3].getWorldQuaternion(leg.rawRotation);
      leg.lift = Math.max(0, leg.raw.y - this.group.position.y);
      if (first) {
        this.groundPoint(leg.anchor.copy(leg.raw)); leg.planted = true; leg.step = null;
        leg.anchorRotation.copy(this.footRotation(leg, frame.yaw, leg.anchor));
      }
    }
    // An idle/attention turn uses one short step at a time, keeping three feet
    // supporting the body. Moving gaits retain their authored lift/contact timing.
    const gait = Object.entries(this.profile.gaits).find(([prefix]) => frame.clip.startsWith(prefix))?.[1];
    if (this.profile.anatomicalLegs && gait?.speed) {
      // Use small replanting strides when turning almost on the spot.
      const stride = T.MathUtils.clamp(frame.speed / Math.max(gait.speed, Math.abs(this.turn) * .65), 0, 1);
      for (const leg of this.legs) {
        const rest = leg.rest.clone().applyAxisAngle(UP, frame.yaw).add(this.group.position);
        leg.raw.x = T.MathUtils.lerp(rest.x, leg.raw.x, stride);
        leg.raw.z = T.MathUtils.lerp(rest.z, leg.raw.z, stride);
      }
    }
    const stationary = frame.speed < .10 && !gait && !/Jump|Bite/.test(frame.clip);
    if (stationary && !this.legs.some(l => l.step) && this.cooldown === 0) {
      let candidate: Leg | undefined, largest = .07 * this.scale;
      for (const leg of this.legs) {
        if (leg.lift > .018 || !leg.planted) continue;
        const error = Math.hypot(leg.raw.x - leg.anchor.x, leg.raw.z - leg.anchor.z);
        if (error > largest) { largest = error; candidate = leg; }
      }
      if (candidate) {
        const predicted = candidate.rest.clone().applyAxisAngle(UP, frame.yaw + this.turn * .26).add(this.group.position);
        this.groundPoint(predicted);
        candidate.step = { time: 0, duration: .32, from: candidate.anchor.clone(), to: predicted,
          rotation: this.footRotation(candidate, frame.yaw + this.turn * .20, predicted) };
        candidate.planted = false; this.stepCount++;
      }
    }
    for (const leg of this.legs) {
      if (leg.step) {
        const step = leg.step; step.time += dt;
        const t = Math.min(1, step.time / step.duration), s = smooth(t);
        leg.target.lerpVectors(step.from, step.to, s);
        leg.target.y = Math.max(leg.target.y, this.ground(leg.target.x, leg.target.z) + (this.profile.clearance ?? .0015)) + .085 * Math.sin(Math.PI * t) ** 2;
        leg.rotation.copy(leg.anchorRotation).slerp(step.rotation, s);
        if (t === 1) {
          leg.anchor.copy(step.to); leg.anchorRotation.copy(step.rotation); leg.step = null;
          leg.planted = true; this.cooldown = .035;
        }
      } else {
        const phase = gait ? ((frame.time / gait.duration - gait.phases[leg.name]) % 1 + 1) % 1 : 0;
        const gaitSwing = !!gait && phase >= gait.duty;
        // A locomotion clip owns contact timing. Sole roll/skin calibration must
        // not release a stance paw and invent additional steps between beats.
        const airborne = gait && this.profile.anatomicalLegs ? gaitSwing : gaitSwing || leg.lift > (leg.planted ? .017 : .012);
        if (airborne && leg.planted) {
          leg.release.copy(leg.anchor).sub(leg.raw); leg.release.y = 0;
          leg.swingTime = 0; leg.planted = false;
        } else if (!airborne && !leg.planted) {
          this.groundPoint(leg.anchor.copy(leg.raw)); leg.planted = true;
          leg.anchorRotation.copy(this.footRotation(leg, frame.yaw, leg.anchor));
        }
        if (leg.planted) {
          leg.target.copy(leg.anchor); leg.rotation.copy(leg.anchorRotation);
        } else {
          leg.swingTime += dt;
          const releaseTime = (this.profile.releaseTime ?? .16) * (this.profile.anatomicalLegs && gait ? gait.duration / 1.6 : 1);
          const swingBlend = smooth(this.profile.anatomicalLegs && gait
            ? T.MathUtils.clamp((phase - gait.duty) / (1 - gait.duty), 0, 1)
            : Math.min(1, leg.swingTime / releaseTime));
          leg.target.copy(leg.raw).addScaledVector(leg.release, 1 - swingBlend);
          leg.target.y = this.ground(leg.target.x, leg.target.z) + leg.lift + (this.profile.clearance ?? .0015);
          leg.rotation.copy(this.footRotation(leg, frame.yaw, leg.target));
          const authored = new T.Quaternion().setFromUnitVectors(UP, leg.normal).multiply(leg.rawRotation);
          leg.rotation.slerp(authored, T.MathUtils.smoothstep(leg.lift, .012, .085));
          if (this.profile.anatomicalLegs) {
            const swingRotation = leg.rotation.clone();
            leg.rotation.copy(leg.anchorRotation).slerp(swingRotation, swingBlend);
          }
        }
      }
    }
    // A broad paw can touch with a claw or heel before its central contact does,
    // especially during toe roll or across a triangulated terrain crease.
    for (const leg of this.legs) if (leg.support.length) {
      let raise = 0;
      for (const local of leg.support) {
        const point = local.clone().multiplyScalar(this.scale).applyQuaternion(leg.rotation).add(leg.target);
        raise = Math.max(raise, this.ground(point.x, point.z) + .0015 - point.y);
      }
      if (raise > 0) { leg.target.y += raise; if (leg.planted) leg.anchor.y = leg.target.y; }
    }
    // Least-squares support plane through the four ground probes. The body
    // follows its pitch, roll and height, rather than hovering level on a slope.
    let xx = 0, xz = 0, zz = 0, sx = 0, sz = 0, sy = 0, xy = 0, zy = 0;
    for (const leg of this.legs) {
      const x = leg.target.x - this.group.position.x, z = leg.target.z - this.group.position.z;
      const y = this.ground(leg.target.x, leg.target.z) - this.group.position.y;
      xx += x * x; xz += x * z; zz += z * z; sx += x; sz += z; sy += y; xy += x * y; zy += z * y;
    }
    const matrix = new T.Matrix3().set(xx, xz, sx, xz, zz, sz, sx, sz, 4);
    const plane = Math.abs(matrix.determinant()) > 1e-7 ? new T.Vector3(xy, zy, sy).applyMatrix3(matrix.invert()) : new T.Vector3(0, 0, sy / 4);
    const normal = new T.Vector3(-plane.x, 1, -plane.y).normalize().applyQuaternion(this.group.quaternion.clone().invert());
    const slope = new T.Quaternion().setFromUnitVectors(UP, normal);
    this.slope.slerp(slope, first ? 1 : 1 - Math.exp(-9 * dt));
    this.height = first ? plane.z : T.MathUtils.damp(this.height, plane.z, 12, dt);
    this.model.quaternion.copy(this.slope).multiply(new T.Quaternion().setFromAxisAngle(new T.Vector3(0, 0, 1), T.MathUtils.clamp(-this.turn * frame.speed * .035, -.10, .10)));
    this.model.position.y = this.height;
    this.group.updateWorldMatrix(true, true);
    for (const joint of this.torso) {
      const axis = UP.clone().applyQuaternion(joint.bone.getWorldQuaternion(new T.Quaternion()).invert());
      joint.bone.quaternion.multiply(new T.Quaternion().setFromAxisAngle(axis, this.turn * joint.weight));
      joint.bone.updateWorldMatrix(false, true);
    }
    // Lower the body just enough for the longest supporting leg to reach. This
    // keeps the links at their authored lengths instead of stretching the skin.
    let lower = 0;
    for (const leg of this.legs) {
      const hip = leg.bones[0].getWorldPosition(new T.Vector3());
      const ankle = leg.target.clone().sub(leg.sole.clone().multiplyScalar(this.scale).applyQuaternion(leg.rotation));
      const reach = leg.lengths.reduce((a, b) => a + b, 0) * (this.profile.anatomicalLegs ? .96 : .985);
      const horizontal = (hip.x - ankle.x) ** 2 + (hip.z - ankle.z) ** 2;
      lower = Math.max(lower, hip.y - ankle.y - Math.sqrt(Math.max(.02, reach * reach - horizontal)));
    }
    this.compression = Math.max(lower, T.MathUtils.damp(this.compression, Math.max(0, lower), 10, dt));
    this.model.position.y -= Math.min(.20, Math.max(0, this.compression));
    this.group.updateWorldMatrix(true, true);
    for (const leg of this.legs) this.solve(leg);
    this.initialized = true;
  }

  private solve(leg: Leg): void {
    const points = leg.bones.map(b => b.getWorldPosition(new T.Vector3())), root = points[0].clone();
    const target = leg.target.clone().sub(leg.sole.clone().multiplyScalar(this.scale).applyQuaternion(leg.rotation));
    if (this.profile.anatomicalLegs) {
      // Keep the authored wrist/hock pitch. Solve the proximal pair toward a
      // body-relative pole, so terrain corrections cannot reverse a joint.
      const distal = points[3].clone().sub(points[2]);
      // On a slope, permit the wrist/hock to extend toward the paw just enough
      // to stay inside the reachable cone. This has one continuous solution;
      // it cannot flip to the opposite side like an unconstrained 3-link solve.
      const reachAxis = target.clone().sub(root), span = reachAxis.length(); reachAxis.normalize();
      const proximalReach = (leg.lengths[0] + leg.lengths[1]) * .985;
      const cone = Math.acos(T.MathUtils.clamp((span * span + leg.lengths[2] ** 2 - proximalReach ** 2) / (2 * span * leg.lengths[2]), -1, 1));
      const angle = distal.angleTo(reachAxis);
      if (angle > cone) {
        const rotation = new T.Quaternion().setFromUnitVectors(distal.clone().normalize(), reachAxis);
        distal.applyQuaternion(new T.Quaternion().slerp(rotation, 1 - cone / angle));
      }
      points[3].copy(target); points[2].copy(target).sub(distal);
      const axis = points[2].clone().sub(root), distance = axis.length(); axis.normalize();
      const pole = new T.Vector3(0, 0, leg.name.startsWith('Fore') ? -1 : 1)
        .applyQuaternion(this.model.getWorldQuaternion(new T.Quaternion()));
      pole.addScaledVector(axis, -pole.dot(axis)).normalize();
      const along = (leg.lengths[0] ** 2 - leg.lengths[1] ** 2 + distance ** 2) / (2 * Math.max(1e-6, distance));
      const bend = Math.sqrt(Math.max(0, leg.lengths[0] ** 2 - along ** 2));
      points[1].copy(root).addScaledVector(axis, along).addScaledVector(pole, bend);
    } else for (let iteration = 0; iteration < 64; iteration++) {
      points[3].copy(target);
      for (let j = 2; j >= 0; j--) points[j].sub(points[j + 1]).setLength(leg.lengths[j]).add(points[j + 1]);
      points[0].copy(root);
      for (let j = 1; j < 4; j++) points[j].sub(points[j - 1]).setLength(leg.lengths[j - 1]).add(points[j - 1]);
      if (points[3].distanceToSquared(target) < 1e-10) break;
    }
    for (let i = 0; i < 3; i++) {
      const joint = leg.bones[i], origin = joint.getWorldPosition(new T.Vector3());
      const from = leg.bones[i + 1].getWorldPosition(new T.Vector3()).sub(origin).normalize();
      const to = points[i + 1].clone().sub(points[i]).normalize();
      const world = new T.Quaternion().setFromUnitVectors(from, to).multiply(joint.getWorldQuaternion(new T.Quaternion()));
      joint.quaternion.copy(joint.parent!.getWorldQuaternion(new T.Quaternion()).invert().multiply(world));
      joint.updateWorldMatrix(false, true);
    }
    const hoof = leg.bones[3];
    hoof.quaternion.copy(hoof.parent!.getWorldQuaternion(new T.Quaternion()).invert().multiply(leg.rotation));
    hoof.updateWorldMatrix(false, true);
    leg.error = hoof.localToWorld(leg.sole.clone()).distanceTo(leg.target);
  }

  snapshot() {
    return { turnRate: this.turn, steps: this.stepCount, bodyHeight: this.model.position.y,
      bodyUp: UP.clone().applyQuaternion(this.model.getWorldQuaternion(new T.Quaternion())).toArray(),
      feet: this.legs.map(l => ({ name: l.name, planted: l.planted, stepping: !!l.step,
        target: l.target.toArray(), sole: l.bones[3].localToWorld(l.sole.clone()).toArray(),
        normal: l.normal.toArray(),
        up: UP.clone().applyQuaternion(l.restRotation.clone().invert()).applyQuaternion(l.bones[3].getWorldQuaternion(new T.Quaternion())).toArray(),
        error: l.error })) };
  }
}
